import React, { useEffect, useMemo, useState } from 'react'
import ReportCalendarLoader from '../../ReportCalendarLoader'
import { isSameDay } from '../../reportCalendarDateUtils'

const WEEKDAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']

const MONTH_NAMES = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
]

const DAY_DOT_VARIANTS = ['urgent', 'yellow', 'default', 'success']

const getMondayBasedOffset = (date) => {
	const day = date.getDay()
	return day === 0 ? 6 : day - 1
}

const buildCalendarDays = (viewDate) => {
	const year = viewDate.getFullYear()
	const month = viewDate.getMonth()
	const firstDayOfMonth = new Date(year, month, 1)
	const lastDayOfMonth = new Date(year, month + 1, 0)
	const offset = getMondayBasedOffset(firstDayOfMonth)
	const gridStart = new Date(year, month, 1 - offset)
	const lastDayOffset = getMondayBasedOffset(lastDayOfMonth)
	const gridEnd = new Date(lastDayOfMonth)
	gridEnd.setDate(lastDayOfMonth.getDate() + (6 - lastDayOffset))
	const days = []
	const current = new Date(gridStart)

	while (current <= gridEnd) {
		days.push({
			date: new Date(current),
			outside: current.getMonth() !== month,
		})
		current.setDate(current.getDate() + 1)
	}

	return days
}

const normalizeEvents = (events) => (
	events.map((event) => ({
		date: event.date instanceof Date ? event.date : new Date(event.date),
		variant: event.variant,
	}))
)

const getDayDotVariants = (date, events) => {
	const variants = new Set(
		events
			.filter((event) => isSameDay(event.date, date))
			.map((event) => event.variant),
	)

	return DAY_DOT_VARIANTS.filter((variant) => variants.has(variant))
}

const ReportCalendarMonthPicker = ({
	selectedDate,
	onChange,
	onViewMonthChange,
	events = [],
	isLoading = false,
}) => {
	const [viewDate, setViewDate] = useState(selectedDate)

	useEffect(() => {
		if (selectedDate) {
			setViewDate(selectedDate)
		}
	}, [selectedDate])

	useEffect(() => {
		onViewMonthChange?.(viewDate)
	}, [viewDate, onViewMonthChange])

	const resolvedEvents = useMemo(() => normalizeEvents(events), [events])
	const calendarDays = useMemo(() => buildCalendarDays(viewDate), [viewDate])

	const handleSelectDate = (date) => {
		onChange?.(date)
	}

	const handlePreviousMonth = () => {
		setViewDate((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))
	}

	const handleNextMonth = () => {
		setViewDate((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))
	}

	const monthLabel = MONTH_NAMES[viewDate.getMonth()]
	const yearLabel = viewDate.getFullYear()

	return (
		<div className='reporting-calendar-month-picker'>
			<div className='reporting-calendar-month-picker__header'>
				<button
					type='button'
					className='reporting-calendar-month-picker__nav-btn'
					onClick={handlePreviousMonth}
					aria-label='Previous month'
				/>
				<div className='reporting-calendar-month-picker__title'>
					<span className='reporting-calendar-month-picker__month'>{monthLabel}</span>
					{' '}
					<span className='reporting-calendar-month-picker__year'>{yearLabel}</span>
				</div>
				<button
					type='button'
					className='reporting-calendar-month-picker__nav-btn reporting-calendar-month-picker__nav-btn--next'
					onClick={handleNextMonth}
					aria-label='Next month'
				/>
			</div>

			<div className='reporting-calendar-month-picker__weekdays' aria-hidden='true'>
				{WEEKDAY_LABELS.map((label, index) => (
					<span
						key={`${label}-${index}`}
						className='reporting-calendar-month-picker__weekday'
					>
						{label}
					</span>
				))}
			</div>

			<div className='reporting-calendar-month-picker__body'>
				<div className='reporting-calendar-month-picker__grid'>
				{calendarDays.map(({ date, outside }) => {
					const selected = isSameDay(date, selectedDate)
					const dayDotVariants = getDayDotVariants(date, resolvedEvents)
					const hasEvents = dayDotVariants.length > 0

					return (
						<button
							key={`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`}
							type='button'
							className={[
								'reporting-calendar-month-picker__day',
								outside && 'reporting-calendar-month-picker__day--outside',
								selected && 'reporting-calendar-month-picker__day--selected',
								hasEvents && 'reporting-calendar-month-picker__day--has-event',
							].filter(Boolean).join(' ')}
							onClick={() => handleSelectDate(date)}
							aria-label={date.toLocaleDateString()}
							aria-pressed={selected}
						>
							<span className='reporting-calendar-month-picker__day-number'>
								{date.getDate()}
							</span>
							<span className='reporting-calendar-month-picker__day-dots' aria-hidden='true'>
								{hasEvents ? (
									dayDotVariants.map((variant) => (
										<span
											key={variant}
											className={[
												'reporting-calendar-month-picker__day-dot',
												`reporting-calendar-month-picker__day-dot--${variant}`,
											].join(' ')}
										/>
									))
								) : (
									<span className='reporting-calendar-month-picker__day-dot reporting-calendar-month-picker__day-dot--placeholder' />
								)}
							</span>
						</button>
					)
				})}
			</div>
			{isLoading && (
				<div className='reporting-calendar-month-picker__loader'>
					<ReportCalendarLoader minHeight={160} />
				</div>
			)}
			</div>
		</div>
	)
}

export default ReportCalendarMonthPicker
